import { DocumentCategory, DocumentSelectionGroup, DocumentSubtype, EditingCategory, ProductKind, ProductSegment, ProductType, ShootingCategory } from 'constants/product'
import { Nullable, NullableStringIndexSignature } from 'models/helpers'
import { ProductCategory, ProductKindDTO } from 'models/product'
import { AddressWithCoordinates, Coordinates, FavouriteCreative, InstructionOptionDTO, PaymentInstructionDTO, PlaceOrderBody, ProductQuantityDTO, TravelCostProduct } from 'models/purchaseFlow'
import { MeasurementOnSiteRequestDTO } from 'models/purchaseFlow/measurementDTO'
import { MeasurementPricingDTO } from 'models/purchaseFlow/measurementPricingDTO'
import { MeasurementQuoteRequestDTO, MeasurementQuoteResponseDTO } from 'models/purchaseFlow/measurementQuoteDTO'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { Country } from 'constants/country'
import { Currency } from 'constants/misc'
import { PaymentMethodTypes } from 'constants/payments'
import { InstructionType } from 'constants/purchaseFlow'
import { Auth0Roles } from 'models/auth'
import { FeeDTO } from 'models/fee'
import { DecimalDTO } from 'models/misc'
import { PaymentIntentDTO } from 'models/stripe'
import { useAPI } from 'utils/API'
import { useAuth0 } from 'utils/auth'
import { logger } from 'utils/logging'

enum Endpoints {
  ORDER_CONFIGURATION_CATEGORY = '/order/configuration/category',
  ORDER_CONFIGURATION_ADDRESS = '/order/configuration/address',
  ORDER_CONFIGURATION_PRODUCT = '/order/configuration/product',
  ORDER_CONFIGURATION_RENOVATION = '/order/configuration/product/renovation',
  ORDER_CONFIGURATION_INSTRUCTION = '/order/configuration/instruction',
  ORDER_CONFIGURATION_RECALCULATE_PRICES = '/order/configuration/recalculate-prices',
  ORDER_CONFIGURATION_MEASUREMENT_QUOTE = '/order/configuration/measurement/quote',
  ORDER_CONFIGURATION_MEASUREMENT_PRICING = '/order/configuration/measurement/pricing',
  PLACE_ORDER = '/order/',
  PLACE_ORDER_WITH_WORKSPACE = '/workspace/{workspaceId}/order/',
  GET_UPDATE_PF_INTENT = '/order/paymentIntent',
  CALCULATE_TRAVEL_COST = '/order/configuration/keyPickup/calculateTravelCost',
  GET_AVAILABLE_CTS = '/order/configuration/creatives',
  GET_AVAILABLE_CTS_WITH_WORKSPACE = '/workspace/{workspaceId}/order/configuration/creatives',
  GET_UPSELL_BILLING_CONFIGURATION = '/order/configuration/upsellBilling/{assignmentId}',
  GET_EXTRA_PRODUCTS = '/order/configuration/product/extra'
}

interface GetTravelCostPayload {
  pickupAddress: AddressWithCoordinates
  assignmentAddress: AddressWithCoordinates
  country: Country
  customerAuth0Id?: string
}

// QUERIES
export function useGetKeyPickupTravelCost(sessionId: Nullable<string>, payload: Partial<GetTravelCostPayload>) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.TRAVEL_COST, QueryType.GET, { sessionId, ...payload }],
    queryFn: () => api.post<{ sessionId: string, data: TravelCostProduct }>(
      Endpoints.CALCULATE_TRAVEL_COST,
      {},
      {
        sessionId,
        data: payload,
      },
      true
    ),
    enabled: !!sessionId && !!payload.assignmentAddress && !!payload.country && !!payload.pickupAddress
  })
}

/**
 * Stateless price estimate for an On-Site Area Report measurement configuration (ISSUE-2188).
 * Pass a null payload to keep the query idle; the backend rejects a non-positive surface area.
 * The admin media type of the endpoint requires the target client's email as a query param
 * (the rate card is country-specific, backend-platform#107) — the query stays idle for an
 * admin until the email is known.
 */
export function useGetMeasurementQuote(payload: Nullable<MeasurementQuoteRequestDTO>, email?: string) {
  const api = useAPI<Endpoints>()
  const { roles } = useAuth0()

  return useQuery({
    queryKey: [EntityKeys.MEASUREMENT_QUOTE, QueryType.GET, { ...payload, email }],
    queryFn: async () => {
      try {
        return await api.post<MeasurementQuoteResponseDTO>(
          Endpoints.ORDER_CONFIGURATION_MEASUREMENT_QUOTE,
          {},
          payload ?? {},
          true,
          email ? { params: { email } } : undefined
        )
      } catch (error) {
        // Surface quote outages in Sentry with the failing configuration: consumers only read `data`,
        // so a failed quote would otherwise silently degrade the customer-facing estimate with no signal
        // LogContext is a closed shape — top-level extras are dropped before transmission, so the payload goes in metadata
        logger.error('Failed to fetch measurement quote', error as Error, {
          context: {
            component: 'useGetMeasurementQuote',
            metadata: { ...payload }
          }
        })

        throw error
      }
    },
    // Each config change is a new query key — keep the previous quote on screen while the fresh one
    // loads, so the cost-assumption panel updates amounts in place instead of collapsing and reloading
    placeholderData: keepPreviousData,
    enabled: !!payload && payload.surfaceArea > 0 && payload.numberOfFloors > 0 && (!roles.isAdmin || !!email)
  })
}

/**
 * Rate-card keys the purchase-flow hints dereference. The DTO is hand-maintained (no codegen, no
 * validation), so a BE rename of any of these arrives as a 200 the hints then silently misprice from —
 * exactly how ISSUE-3007's predecessor field stayed invisible. cellarThirdPlusLevel is deliberately
 * absent: it is null by contract while the rate is not offered in the client's country.
 */
const MEASUREMENT_PRICING_REQUIRED_KEYS = [
  'apartmentBase',
  'housePerFloor',
  'houseFloorsIncluded',
  'firstResidentialUnit',
  'additionalResidentialUnit',
  'additionalFloor',
  'cellar',
  'atticSecondLevel',
  'atticThirdPlusLevel',
  'areaSurchargeRatePerSqm',
  'currency'
] as const satisfies ReadonlyArray<keyof MeasurementPricingDTO>

/**
 * Rate card for the On-Site Area Report price hints (ISSUE-2702) — SF-synced unit prices, area
 * threshold and VAT, readable before any quote exists. Country resolves from the logged-in user;
 * the admin media type requires the target client's email as a query param (backend-platform#107),
 * so the query stays idle for an admin until the email is known.
 *
 * [isEnabled] lets a caller that only sometimes shows a rate — the product step, which shows a
 * "starting at" price when the catalogue carries the measurement product — skip the request entirely
 * instead of fetching (and Sentry-logging failures of) a rate card nobody will read.
 */
export function useGetMeasurementPricing(email?: string, isEnabled: boolean = true) {
  const api = useAPI<Endpoints>()
  const { roles } = useAuth0()

  return useQuery({
    queryKey: [EntityKeys.MEASUREMENT_PRICING, QueryType.GET, { email }],
    queryFn: async () => {
      try {
        const response = await api.get<MeasurementPricingDTO>(
          Endpoints.ORDER_CONFIGURATION_MEASUREMENT_PRICING,
          {},
          true,
          email ? { params: { email } } : undefined
        )

        // A 200 with a renamed/dropped key would otherwise be cached as a good card for the whole
        // session (staleTime: Infinity) and misprice every hint with no signal anywhere
        const missingKeys = MEASUREMENT_PRICING_REQUIRED_KEYS.filter(key => response.data[key] == null)
        if (missingKeys.length > 0) {
          logger.warn('Measurement rate card response is missing required keys — the hand-written DTO drifted from the BE contract', undefined, {
            context: { component: 'useGetMeasurementPricing', metadata: { missingKeys } }
          })
        }

        return response
      } catch (error) {
        // Hint prices silently disappear on a failed rate card — surface the outage in Sentry
        logger.error('Failed to fetch measurement pricing', error as Error, {
          context: { component: 'useGetMeasurementPricing' }
        })

        throw error
      }
    },
    // The rate card changes only on a finance retune (SF price + product sync) — cache it for the session
    staleTime: Infinity,
    enabled: isEnabled && (!roles.isAdmin || !!email)
  })
}

interface GetPurchaseProductRenovationPayload {
  email: string
  category: ProductCategory
  countryCode: string
  regionCode: string
  postalCode: string
  city: Nullable<string>
}

export function useGetRenovationProducts(sessionId: Nullable<string>, payload: Partial<GetPurchaseProductRenovationPayload>) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.ORDER, QueryType.GET, { sessionId, ...payload }],
    queryFn: () => api.post<{ sessionId: string, data: ProductKindDTO }>(
      Endpoints.ORDER_CONFIGURATION_RENOVATION,
      {},
      {
        sessionId,
        data: payload
      },
      true
    ),
    enabled: !!sessionId && !!payload.category && !!payload.email
  })
}

export function useGetUpsellBillingConfiguration(assignmentId?: Nullable<string>, enabled?: boolean) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.UPSELL_CONFIG, QueryType.GET, { assignmentId }],
    queryFn: () => api.get<PaymentInstructionDTO>(
      Endpoints.GET_UPSELL_BILLING_CONFIGURATION,
      { assignmentId: assignmentId ?? '' },
      true
    ),
    enabled: !!assignmentId && enabled,
  })
}

// MUTATIONS

export type PurchaseSessionWrapper<T> = AxiosResponse<{
  sessionId: string
  data: T
}>

interface CategoryConfig {
  defaultCategory: ShootingCategory
  shootingCategories: ShootingCategory[]
  editingCategories: EditingCategory[]
  documentCategories: DocumentCategory[]
  countryCode: Country
  visible: boolean
}

export function useGetCategoryConfig$() {
  const api = useAPI<Endpoints>()

  return getMutation<PurchaseSessionWrapper<CategoryConfig>, { email: string | undefined }>(
    (body) => api.post(
      Endpoints.ORDER_CONFIGURATION_CATEGORY,
      {},
      body,
      true
    )
  )
}

interface AddressConfig {
  visible: boolean
  defaultAddress: Nullable<string>
  defaultCountryCode: Nullable<Country>
  defaultCoordinates: Nullable<Coordinates>
}

interface AddressConfigPayload {
  sessionId: Nullable<string>
  email?: string
}

export function useGetAddressConfig$() {
  const api = useAPI<Endpoints>()

  return getMutation<PurchaseSessionWrapper<AddressConfig>, AddressConfigPayload>(
    ({ sessionId, ...data }) => api.post(
      Endpoints.ORDER_CONFIGURATION_ADDRESS,
      {},
      {
        sessionId,
        data,
      },
      true
    )
  )
}

export enum OptionProductType {
  INTEGER = 'INTEGER',
  BOOLEAN = 'BOOLEAN',
  TEXT = 'TEXT',
  DATETIME = 'DATETIME',
}

export interface CatalogueOptionProduct {
  id: number
  /** Stable Product2 code — the identifier the API prefers over the legacy numeric id. Null while a product has no mirror yet. */
  productCode: Nullable<string>
  /** @deprecated */
  duration: number
  timeOnSiteMin: Nullable<number>
  timeOnSiteMax: Nullable<number>
  feePrice: FeeDTO
  price: number
  kind: ProductKind
  value: number
  type: OptionProductType
}

export interface CatalogueBundledProduct {
  id: number,
  category: null,
  type: ProductType,
  kind: ProductKind,
  /** A package never carries the flag itself — only its members do, so the land-registration gate reads these. */
  isLandRegistrationNeeded: boolean,
  /** Subtype of an authorities-document member, or null — same backend-computed field as on the parent. */
  documentSubtype?: Nullable<DocumentSubtype>,
}

export interface CatalogueProduct {
  id: number
  kind: ProductKind
  type: ProductType
  feePrice: FeeDTO
  originalPrice: Nullable<FeeDTO>
  price: number
  /** @deprecated */
  shootingDuration: number
  timeOnSiteMin: Nullable<number>
  timeOnSiteMax: Nullable<number>
  version: string
  options: Array<CatalogueOptionProduct>
  isBestSeller: boolean
  isLandRegistrationNeeded: boolean
  /** Whether this product is AI photo editing — backend-computed (ISSUE-2539), read instead of `type` */
  isAiPhotoEditing: boolean
  /** Subtype of an authorities-document product, or null — backend-computed (ISSUE-2673), read instead of `type` */
  documentSubtype?: Nullable<DocumentSubtype>
  /** Mutually-exclusive selection group, or null — backend-computed and only emitted while the DocEstate bewilu migration flag is on (ISSUE-2866) */
  documentSelectionGroup?: Nullable<DocumentSelectionGroup>
  parentProductIds: number[]
  bundledProducts: CatalogueBundledProduct[]
}

export interface PurchaseProductCatalogue {
  visible: boolean
  currency: Currency
  decimalVat: DecimalDTO
  decimalDiscount: DecimalDTO
  segments: NullableStringIndexSignature<
    {
      key: ProductSegment
      productTypes: Partial<Record<ProductType, {
        key: ProductType
        products: Array<Omit<CatalogueProduct, 'type'>>
      }>>
    }
  >
}

interface GetPurchaseProductCataloguePayload {
  sessionId: Nullable<string>
  email: Nullable<string>
  category: Nullable<ProductCategory>
  countryCode: Nullable<string>
  city: Nullable<string>
  regionCode: Nullable<string>
  postalCode: Nullable<string>
  revenueChannel?: Nullable<string>
  realEstatePropertyId?: Nullable<string>
  selectedProducts?: Array<{ productId: number, quantity: number }>
}

export function useGetProducts$() {
  const api = useAPI<Endpoints>()

  return getMutation<PurchaseSessionWrapper<PurchaseProductCatalogue>, GetPurchaseProductCataloguePayload>(
    ({ sessionId, ...data }) => api.post(
      Endpoints.ORDER_CONFIGURATION_PRODUCT,
      {},
      {
        sessionId,
        data,
      },
      true
    )
  )
}

export interface PFInstructionOption {
  id: number
  kind: ProductKind
  isPrimary: boolean
  price: number
  feePrice: FeeDTO
  fields: Array<{
    key: string
    type: string
    value: string
  }>
}

interface InstructionsConfig {
  visible: boolean
  currency: Currency
  timezone: string
  instructionTypes: Partial<Record<InstructionType, {
    key: InstructionType
    instructionOptions: Array<InstructionOptionDTO>
  }>>
}

interface GetInstructionConfigPayload {
  sessionId: Nullable<string>
  email: Nullable<string>
  countryCode: Nullable<string>
  category: Nullable<ProductCategory>
  coordinates: Nullable<Nullable<Coordinates>>
  productKinds: Nullable<ProductKind[]>
  products: Nullable<object[]>
  area: Nullable<string>
  city: Nullable<string>
  /** Workspace the order is placed in; decides which invoice-by-email variant is offered */
  workspaceId: Nullable<string>
}

export function useGetInstructionConfig$() {
  const api = useAPI<Endpoints>()

  return getMutation<PurchaseSessionWrapper<InstructionsConfig>, GetInstructionConfigPayload>(
    ({ sessionId, ...data }) => api.post(
      Endpoints.ORDER_CONFIGURATION_INSTRUCTION,
      {},
      {
        sessionId,
        data,
      },
      true
    )
  )
}

interface GetAvailableCreativesPayload {
  role: Auth0Roles
  workspaceId?: string
  sessionId: string
  selectedProductIds: number[]
  address: string
  coordinates: Coordinates
  /** ISO 3166-1 alpha-2 country of the shooting address — the same value place-order sends. Omitted on the wire when unknown. */
  countryCode?: string
  clientUserId: string
  name?: string
  page?: number
  size?: number
}

export interface GetAvailableCreativesResponse {
  availableCreatives: FavouriteCreative[]
  personalCreatives: FavouriteCreative[]
  workspaceCreatives: FavouriteCreative[]
}

export function useGetAvailableCTs$() {
  const api = useAPI<Endpoints>()

  return getMutation<PurchaseSessionWrapper<GetAvailableCreativesResponse>, GetAvailableCreativesPayload>(
    ({ sessionId, workspaceId, role, name, page = 0, size = 10, countryCode, ...data }) => api.post(
      role.isAdmin ? Endpoints.GET_AVAILABLE_CTS : Endpoints.GET_AVAILABLE_CTS_WITH_WORKSPACE,
      { workspaceId: workspaceId || '' },
      {
        sessionId,
        // An empty string is "not known yet", not a country: the backend keeps its own fallback when the key is absent
        data: countryCode ? { ...data, countryCode } : data,
      },
      true,
      {
        params: {
          page,
          size,
          name,
        }
      }
    )
  )
}

export function usePlaceOrder() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<{ id: number }>, PlaceOrderBody>(
    ({ workspaceId, role, ...payload }) => api.post(
      (workspaceId ? Endpoints.PLACE_ORDER_WITH_WORKSPACE : Endpoints.PLACE_ORDER),
      { workspaceId: workspaceId || '' },
      payload,
      true
    ),
    // The next order click reads the property re-order context from the cache
    (client) => client.invalidateQueries({ queryKey: [EntityKeys.PROPERTY_REORDER_CONTEXT] })
  )
}

interface CreateUpdatePFIntentPayload {
  sessionId: string
  isUpdate: boolean
  products: ProductQuantityDTO[]
  paymentMethod: PaymentMethodTypes
  invoicingAddressId?: string
  /** Same configuration place-order sends — the BE prices the measurement line with its engine, not from the catalogue entry */
  measurementOnSiteRequest?: MeasurementOnSiteRequestDTO
}

export function useCreateUpdatePFIntent() {
  const api = useAPI<Endpoints>()

  return getMutation<PurchaseSessionWrapper<PaymentIntentDTO>, CreateUpdatePFIntentPayload>(
    ({ sessionId, isUpdate, invoicingAddressId, ...data }) => {

      if (isUpdate) {
        return api.put(
          Endpoints.GET_UPDATE_PF_INTENT,
          {},
          {
            sessionId,
            data: {
              ...data,
              invoicingAddressId,
            },
          },
          true
        )
      } else {
        return api.post(
          Endpoints.GET_UPDATE_PF_INTENT,
          {},
          {
            sessionId,
            data: {
              ...data,
              invoicingAddressId,
              paymentMethodTypes: [data.paymentMethod]
            },
          },
          true
        )
      }
    }
  )
}

interface ExtraProductsPayload {
  sessionId: string
  email?: string,
  category: ProductCategory,
  countryCode?: string,
  regionCode: string,
  postalCode: string,
  city?: string
}

export function useGetExtraProducts$() {
  const api = useAPI<Endpoints>()

  return getMutation<PurchaseSessionWrapper<Array<CatalogueProduct>>, ExtraProductsPayload>(
    ({ sessionId, ...data }) => api.post(
      Endpoints.GET_EXTRA_PRODUCTS,
      {},
      {
        sessionId,
        data,
      },
      true
    )
  )
}

// RECALCULATE PRICES

export interface RecalculatePricesProduct {
  id: number
  count: number
  feePrice: FeeDTO
}

export interface RecalculatePricesResponse {
  currency: Currency
  vatRate: number
  discount: number
  products: RecalculatePricesProduct[]
}

interface RecalculatePricesPayload {
  sessionId: string
  products: ProductQuantityDTO[]
  invoicingAddressId: string
  email?: string
}

export function useRecalculatePrices() {
  const api = useAPI<Endpoints>()

  return getMutation<PurchaseSessionWrapper<RecalculatePricesResponse>, RecalculatePricesPayload>(
    ({ sessionId, email, ...data }) => api.post(
      Endpoints.ORDER_CONFIGURATION_RECALCULATE_PRICES,
      {},
      {
        sessionId,
        data: {
          ...data,
          ...(email && { email }),
        },
      },
      true
    )
  )
}
